import { ReactComponent as WhatsAppIcon } from '@shared/assets/icons/ic-whatsapp-black.svg'
import { ArrowRight, AudioLines, Bell, List, Network, Phone } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'router-v6'
import { useIndiaComplianceFirstRun } from 'v2/pages/settings/phone-numbers/use-india-compliance-first-run'

interface SetupItem {
  icon: ReactNode
  title: string
  description: string
  to: string
}

interface SetupColumnData {
  heading: string
  subtitle: string
  items: SetupItem[]
}

const setupColumns = (needsKyc: boolean): SetupColumnData[] => [
  {
    heading: 'Build',
    subtitle: 'Connect your voice agent to Plivo over WebSocket or SIP.',
    items: [
      {
        icon: <AudioLines className="size-3.5" />,
        title: 'Audio Streaming',
        description: 'Stream calls to your voice agent',
        to: '/applications',
      },
      {
        icon: <Network className="size-3.5" />,
        title: 'SIP Trunking',
        description: 'Connect your SIP infrastructure',
        to: '/sip-trunking',
      },
    ],
  },
  {
    heading: 'Deploy',
    subtitle: 'Put your voice agent on a phone or WhatsApp number.',
    items: [
      {
        icon: <Phone className="size-3.5" />,
        title: 'Buy Phone Number',
        // No banner: only renting a number needs the KYC, which starts on Phone Numbers.
        description: needsKyc ? 'Complete KYC to rent a number' : 'Phone numbers for Voice and SMS',
        to: '/phone-numbers',
      },
      {
        // The logo carries its own padding: one size up to match the others, with -m-px
        // so it still takes their 14px slot and the titles stay aligned.
        icon: <WhatsAppIcon className="-m-px size-4" />,
        title: 'Connect WhatsApp Number',
        description: 'WABA profiles, numbers and templates',
        to: '/whatsapp',
      },
    ],
  },
  {
    heading: 'Monitor',
    subtitle: "Track your agent's calls and get alerts on issues.",
    items: [
      {
        icon: <List className="size-3.5" />,
        title: 'Logs',
        description: 'View calls and messaging logs',
        to: '/logs',
      },
      {
        icon: <Bell className="size-3.5" />,
        title: 'Alerting',
        description: 'Configure alerts and notifications',
        to: '/alerting',
      },
    ],
  },
]

const SetupLinkItem = ({ icon, title, description, to }: SetupItem) => (
  <Link
    to={to}
    data-testid={`setup-${to.slice(1)}-link`}
    className="group flex h-full items-center gap-3 rounded-md border border-st2 bg-background px-4 py-3 transition-colors hover:border-brand focus-visible focus-visible:border-brand"
  >
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        {/* Bare 14px glyph with one brand colour for every icon. brand-text (not brand) because
            this is an icon, which the kit lifts to #4d6aff on dark for contrast. */}
        <span className="shrink-0 text-brand-text" aria-hidden="true">
          {icon}
        </span>
        <span className="text-p-600 min-w-[120px] flex-1 break-words">{title}</span>
      </div>
      <p className="mt-1 text-xs-400 text-secondary">{description}</p>
    </div>
    <ArrowRight
      aria-hidden="true"
      className="size-3.5 shrink-0 text-tertiary transition-[color,transform] duration-200 group-hover:translate-x-0.5 group-hover:text-brand-text"
    />
  </Link>
)

const SetupColumn = ({ heading, subtitle, items }: SetupColumnData) => (
  <div className="flex flex-col gap-3 lg:row-span-3 lg:grid lg:grid-rows-[subgrid]">
    {/* mb-1 gives the heading room without spreading the cards apart. */}
    <div className="mb-1">
      {/* One step above the cards' own titles (text-p-600, 14px) so the
          column heading reads as their parent rather than a peer — but at
          weight 500, matching the page's own h2-500 "Welcome". A 600 here
          made the sub-heading heavier than the title above it. */}
      <h3 className="text-h3-500">{heading}</h3>
      <p className="mt-1 text-xs-400 text-secondary">{subtitle}</p>
    </div>
    {items.map((item) => (
      <SetupLinkItem key={item.title} {...item} />
    ))}
  </div>
)

export const SetupSection = () => {
  // Phone Numbers' own gate, so the tile asks for a KYC exactly when that page does.
  const { isKycRequired } = useIndiaComplianceFirstRun()
  return (
    <section className="overflow-hidden rounded-lg border border-st2">
      <div className="flex h-10 items-center justify-between border-b border-st2 bg-background dark:bg-bg2 px-4">
        <h2 className="font-jetbrains text-s-400 text-secondary">
          <span className="text-tertiary">/</span>get-started
        </h2>
        <span className="flex items-center gap-1.5 font-jetbrains text-label uppercase text-secondary">
          <span className="size-1.5 rounded-full bg-brand" aria-hidden="true" />
          setup
        </span>
      </div>
      <div className="grid grid-cols-1 gap-x-4 gap-y-6 p-5 lg:grid-cols-3">
        {setupColumns(isKycRequired).map((column) => (
          <SetupColumn key={column.heading} {...column} />
        ))}
      </div>
    </section>
  )
}
